import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import * as Yup from 'yup';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  Paper,
  MenuItem,
  Input,
  InputAdornment,
  IconButton,
  Backdrop,
  CircularProgress,
  Stack,
  FormControlLabel,
  Switch
} from '@material-ui/core';
import ReceiptIcon from '@material-ui/icons/Receipt';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import SearchIcon from '@material-ui/icons/Search';
import { useFormik, Form, FormikProvider } from 'formik';
import { baseUrl, services } from '../../utils/constants';
import { useClientContext } from '../../context/client_context';
import { useClaimsContext } from '../../context/claims_context';
import { useConstantsContext } from '../../context/constants_context';
import Controls from '../controls/Controls';
import ClaimSubform from './ClaimSubform';
import ClaimsDocs from './ClaimsDocs';
import ClaimsLineInit from './ClaimsLineInit';

export default function ClaimsSelf() {
  const dateToday = new Date();
  const { payCycles } = useConstantsContext();
  const { setCurrentClaim, currentClaim, claimNo, currentBatch, setClaimNo, clearLineItem } =
    useClaimsContext();
  const { dependantList, setDependantList } = useClientContext();
  const [docSearchCode, setSearchCode] = useState('');
  const [providers, setProviders] = useState([]);
  const [memberCode, setMemberCode] = useState('');
  const [memberSearchName, setSearchName] = useState('');
  const [switchChecked, setSwitchChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [openPopupDiag, setOpenPopupDiag] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    subTitle: '',
    heading: '',
    color: '#000000'
  });

  useEffect(() => {
    getClaimById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependantList]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClaim]);

  useEffect(() => {
    if (currentClaim.parent_Cif) populateDependants(currentClaim.parent_Cif);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClaim.parent_Cif]);

  const ClaimsSchema = Yup.object().shape({
    pay_Cycle_Date: Yup.number()
      .required('you must specify the Transacting Month')
      .moreThan(0, 'you must specify the Transacting Month'),
    provider_Id: Yup.number()
      .required('Provider Id is required')
      .moreThan(0, 'Provider has not been updated'),
    cif_Number: Yup.number()
      .required('Patient is required')
      .moreThan(0, 'Patient has not been selected'),
    service_Type_id: Yup.number()
      .required('Service is required')
      .moreThan(0, 'Service has not been selected'),
    medFundNumber: Yup.string()
      .min(2, 'Member Fund # is too Short!')
      .max(8, 'Member Fund # is too Long!')
      .required('Member Fund # required')
  });

  const formik = useFormik({
    initialValues: currentClaim,
    enableReinitialize: true,
    validationSchema: ClaimsSchema,
    onSubmit: (values) => {
      console.log('values found', values);
      if (values.cif_Number === '') {
        notifyPopup(`Patient # not Selected, please select Patient!`, 'error');
      } else {
        saveClaim(values);
      }
    }
  });

  const { submitForm } = formik;

  const handleSubmit = () => {
    console.log('came here to submit');
    submitForm();
  };

  const getMemberInfo = async (event) => {
    if (memberCode === '') {
      notifyPopup(`No Employee # Selected, please enter number to seach for!`, 'error');
      return;
    }
    setLoading(true);
    populateProviders(currentBatch.provider_Id);
    await axios(`${baseUrl}/api/client/GetMembersByFundNo?medFundNo=${memberCode}`).then(
      (result) => {
        if (result.replyStatus === 1) {
          notifyPopup(`Member not Found!: ${result.replyMessage}`, 'error');
        } else if (result.data.result.length === 0) {
          notifyPopup(`Member not Found! Please enter correct Policy #`, 'error');
        } else {
          console.log('returned members', result);
          console.log('returned members cif', result.data.result[0]);
          setDependantList(result.data.result);
          formik.setFieldValue('parent_Cif', result.data.result[0].cif_number);
          formik.setFieldValue('medFundNumber', result.data.result[0].memberShipNo);
          formik.setFieldValue('scheme_id', result.data.result[0].principalSchemeID);
          formik.setFieldValue('memberType_id', result.data.result[0].memberType_ID);
          formik.setFieldValue('group_id', result.data.result[0].group_id);
          setSearchName(`${result.data.result[0].surname} ${result.data.result[0].firstName}`);
          const date1 = moment(result.data.result[0].dateOfBirth);
          const date2 = moment();
          const diff = date2.diff(date1, 'years');
          formik.setFieldValue('currentAge', diff);

          formik.setFieldValue('batchID', currentBatch.batchID);
          formik.setFieldValue('provider_Id', currentBatch.provider_Id);
          formik.setFieldValue('pay_Cycle_Date', currentBatch.payCycleId);
          formik.setFieldValue('status_Id', currentBatch.batchStatus_Id);
          formik.setFieldValue('workStation', window.location.hostname);
          formik.setFieldValue('currencyId', 3);
          formik.setFieldValue('userId', sessionStorage.getItem('oldRefId'));
          if (currentBatch.isSelfPayment) {
            setSwitchChecked(!switchChecked);
            formik.setFieldValue('self_Payment', !switchChecked);
          }
          setLoading(false);
        }
      },
      (error) => {
        notifyPopup(`Error in getMemberInfo!: ${error}`, 'error');
      }
    );
  };

  const populateProviders = async (providerId) => {
    if (providerId === '') {
      return;
    }
    setLoading(true);
    await axios(`${baseUrl}/api/setting/GetProvidersById?providerId=${providerId}`).then(
      (result) => {
        setLoading(false);
        if (result.replyStatus === 1) {
          notifyPopup(`Provider not Found!: ${result.replyMessage}`, 'error');
        } else {
          setProviders(result.data);
        }
      },
      (error) => {
        notifyPopup(`Error in populateProviders!: ${error}`, 'error');
      }
    );
  };

  const populateDependants = async (cifNumber) => {
    if (cifNumber === '') {
      return;
    }
    setLoading(true);
    await axios(`${baseUrl}/api/client/GetMembersByParentCif?cifNumber=${cifNumber}`).then(
      (result) => {
        setLoading(false);
        if (result.replyStatus === 1) {
          notifyPopup(`Provider not Found!: ${result.replyMessage}`, 'error');
        } else {
          setDependantList(result.data.result);
          setSearchName(result.data.result[0].accName);
        }
      },
      (error) => {
        notifyPopup(`Error in populateDependants!: ${error}`, 'error');
      }
    );
  };

  const getClaimById = async (event) => {
    if (claimNo === -1) {
      return;
    }
    setLoading(true);

    populateProviders(currentBatch.provider_Id);
    await axios(`${baseUrl}/api/claims/getClaimById?claimId=${claimNo}`).then(
      (result) => {
        setLoading(false);
        console.log('returned claim info', result);
        setCurrentClaim(result.data.result);
      },
      (error) => {
        notifyPopup(`Error in Getting Claim!: ${error}`, 'error');
      }
    );
  };

  const memberStateEnquiry = async (e) => {
    formik.setFieldValue('cif_Number', e);
    setLoading(true);

    await axios(`${baseUrl}/api/client/memberStateEnquiry?cif_number=${e}`).then(
      (result) => {
        setLoading(false);
        console.log('returned query info', result);
        if (result.data.result === undefined || result.data.result === null)
          notifyPopup(`Client Terminated or Not Qualifying.  Claim will not be honoured`, 'error');
      },
      (error) => {
        notifyPopup(`Error in Member Query: ${error}`, 'error');
      }
    );
  };

  const saveClaim = async (client) => {
    setLoading(true);
    const jsonClient = [];
    jsonClient.push(client);
    console.log('claims stringified now', JSON.stringify(jsonClient[0]));
    fetch(`${baseUrl}/api/claims/saveClaim`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(jsonClient[0])
    })
      .then((res) => res.json())
      .then(
        (res) => {
          console.log('returned claim', res);
          setLoading(false);
          setCurrentClaim(res);
          setClaimNo(res.claim_ID);
          notifyPopup(`Claim Saved Successfully!`, 'success');
        },
        (error) => {
          setLoading(false);
          notifyPopup(`Error in Saving Claim!: ${error}`, 'error');
        }
      );
  };

  const getDoctorInfo = async (event) => {
    if (docSearchCode === '') {
      notifyPopup(`No AHFOZ # Selected, please enter number to seach for!`, 'error');
      return;
    }
    setLoading(true);
    await axios(`${baseUrl}/api/setting/GetProvider?namasNo=${docSearchCode}`).then(
      (result) => {
        setLoading(false);
        if (result.replyStatus === 1) {
          notifyPopup(`Provider not Found!: ${result.replyMessage}`, 'error');
        } else {
          console.log('returned doc', result);
          formik.setFieldValue('doctor_Code', result.data.namasNo);
          formik.setFieldValue('provider_Id', result.data.provider_id);
          formik.setFieldValue('userRefId', sessionStorage.getItem('oldRefId'));
          // setSearchName(result.data.provider);
          populateProviders(result.data.provider_id);
        }
      },
      (error) => {
        notifyPopup(`Error in Searching for Medical Practise!: ${error}`, 'error');
      }
    );
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleLoadingClose = () => {
    setLoading(false);
  };

  const closePopup = () => {
    setOpenPopup(false);
  };

  const closePopupDiag = () => {
    setOpenPopupDiag(false);
  };

  const newLine = () => {
    clearLineItem();
    setOpenPopupDiag(true);
  };

  const notifyPopup = (notifyMessage, notifyType) => {
    setNotify({
      isOpen: true,
      message: `${notifyMessage}`,
      type: `${notifyType}`
    });
  };

  const { errors, touched } = formik;

  /* eslint-disable camelcase */
  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate>
        <Typography component="span" variant="h6" gutterBottom>
          Process Claim
        </Typography>
        <Card
          sx={{
            m: '30px 100px'
          }}
        >
          <CardContent>
            <Backdrop
              sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={loading}
              onClick={handleLoadingClose}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
            <Grid container spacing={2}>
              <Grid item xs={8} sm={8}>
                <Paper>
                  <Grid margin={2} container spacing={2}>
                    <Grid item xs={3} sm={3} mr={2}>
                      <FormControl>
                        <InputLabel htmlFor="memberCodem">Search Member</InputLabel>
                        <Input
                          id="memberCode"
                          type="text"
                          value={memberCode}
                          onChange={(e) => {
                            setMemberCode(e.target.value);
                          }}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onMouseDown={handleMouseDownPassword}
                                onClick={() => getMemberInfo()}
                              >
                                <SearchIcon />
                              </IconButton>
                            </InputAdornment>
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <FormControl fullWidth>
                        <InputLabel htmlFor="memberSearchName">Member Name</InputLabel>
                        <Input
                          id="memberSearchName"
                          type="text"
                          value={memberSearchName}
                          disabled
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={3} sm={5}>
                      <Controls.Input name="medFundNumber" label="Policy #" disabled />
                    </Grid>
                    <Grid item xs={4} sm={5} ml={2}>
                      <FormControl>
                        <FormControlLabel
                          control={
                            <Switch
                              name="self_Payment"
                              value={formik.values.self_Payment}
                              size="small"
                              checked={switchChecked}
                              onChange={(e) => {
                                setSwitchChecked(!switchChecked);
                                formik.setFieldValue('self_Payment', !switchChecked);
                              }}
                            />
                          }
                          label="Self Payment"
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={4} sm={5}>
                      <FormControl>
                        <InputLabel htmlFor="docSearchCode">Search Provider</InputLabel>
                        <Input
                          id="docSearchCode"
                          type="text"
                          value={docSearchCode}
                          onChange={(e) => {
                            setSearchCode(e.target.value);
                          }}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onMouseDown={handleMouseDownPassword}
                                onClick={() => getDoctorInfo()}
                              >
                                <SearchIcon />
                              </IconButton>
                            </InputAdornment>
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={4} sm={5}>
                      <FormControl fullWidth>
                        <InputLabel id="providerText">Provider</InputLabel>
                        <Select
                          id="provider_Id"
                          value={formik.values.provider_Id}
                          name="provider_Id"
                          fullWidth
                          size="small"
                          variant="outlined"
                          onChange={(e) => {
                            formik.setFieldValue('provider_Id', e.target.value);
                          }}
                          error={Boolean(touched.provider_Id && errors.provider_Id)}
                          helperText={touched.provider_Id && errors.provider_Id}
                        >
                          <MenuItem key="" value="">
                            Not Selected
                          </MenuItem>
                          {providers.map((item) => (
                            <MenuItem key={item.provider_id} value={item.provider_id}>
                              {item.provider}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4} sm={5}>
                      <FormControl fullWidth>
                        <InputLabel id="patientlbl">Patient</InputLabel>
                        <Select
                          id="cif_Number"
                          value={formik.values.cif_Number}
                          name="cif_Number"
                          fullWidth
                          size="small"
                          variant="outlined"
                          onChange={(e) => {
                            memberStateEnquiry(e.target.value);
                          }}
                          error={Boolean(touched.cif_Number && errors.cif_Number)}
                          helperText={touched.cif_Number && errors.cif_Number}
                        >
                          <MenuItem key="" value="">
                            Not Selected
                          </MenuItem>
                          {dependantList.map((item) => (
                            <MenuItem key={item.cif_number} value={item.cif_number}>
                              {item.suffix} {item.surname} {item.firstName} {item.dateOfBirth}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={3} sm={5}>
                      <Controls.Input name="invoice_No" label="Invoice #" />
                    </Grid>
                    <Grid item xs={4} sm={5}>
                      <FormControl fullWidth>
                        <InputLabel id="service_Type_IdTxt">Service Type</InputLabel>
                        <Select
                          id="service_Type_id"
                          value={formik.values.service_Type_id}
                          name="service_Type_id"
                          fullWidth
                          size="small"
                          variant="outlined"
                          onChange={(e) => {
                            formik.setFieldValue('service_Type_id', e.target.value);
                          }}
                          error={Boolean(touched.service_Type_id && errors.service_Type_id)}
                          helperText={touched.service_Type_id && errors.service_Type_id}
                        >
                          <MenuItem key="" value="">
                            Not Selected
                          </MenuItem>
                          {services.map((item) => (
                            <MenuItem key={item.refId} value={item.refId}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={3} sm={5}>
                      <Controls.Input name="diagnosis" label="Diagnosis" disabled />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4} mt={2}>
                <Paper variant="outlined">
                  <Grid item xs={12} sm={12} margin={2}>
                    <Controls.InputNumber name="claim_ID" label="Claim #" disabled />
                  </Grid>
                  <Grid item xs={12} sm={12} margin={2}>
                    <Controls.InputNumber name="batchID" label="Batch #" disabled />
                  </Grid>
                  <Grid item xs={12} sm={12} margin={2}>
                    <FormControl fullWidth>
                      <InputLabel id="payCycleLbl">Pay Period</InputLabel>
                      <Select
                        id="pay_Cycle_Date"
                        value={formik.values.pay_Cycle_Date}
                        name="pay_Cycle_Date"
                        fullWidth
                        size="small"
                        variant="outlined"
                        onChange={(e) => {
                          formik.setFieldValue('pay_Cycle_Date', e.target.value);
                        }}
                      >
                        <MenuItem key="" value="">
                          Not Selected
                        </MenuItem>
                        {payCycles.map((item) => (
                          <MenuItem key={item.payCycleId} value={item.payCycleId}>
                            {item.payDate}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} m={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb="3">
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  startIcon={<ReceiptIcon />}
                  onClick={() => handleSubmit()}
                >
                  Save Claim
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  disabled={formik.values.diagnosis_Id > 0 || formik.values.claim_ID < 1}
                  startIcon={<LocalHospitalIcon />}
                  onClick={() => newLine()}
                >
                  Add Diagnosis
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  startIcon={<ReceiptIcon />}
                  disabled={
                    formik.values.diagnosis_Id === 0 ||
                    formik.values.claim_ID < 1 ||
                    formik.values.status_Id === 5
                  }
                  onClick={() => setOpenPopup(true)}
                >
                  Add Line
                </Button>
              </Stack>
            </Grid>
            <Controls.Notification notify={notify} setNotify={setNotify} />
            <Controls.ConfirmDialog
              confirmDialog={confirmDialog}
              setConfirmDialog={setConfirmDialog}
            />
            <Controls.Popup
              title="Add Detail Line"
              openPopup={openPopup}
              setOpenPopup={setOpenPopup}
            >
              <ClaimsLineInit
                closePopup={closePopup}
                claim_ID={claimNo}
                service_Type_Id={currentClaim.service_Type_id}
              />
            </Controls.Popup>
            <Controls.Popup
              title="Add Diagnosis"
              openPopup={openPopupDiag}
              setOpenPopup={setOpenPopupDiag}
            >
              <ClaimsDocs closePopup={closePopupDiag} claim_ID={claimNo} />
            </Controls.Popup>
          </CardContent>
        </Card>
        <ClaimSubform />
      </Form>
    </FormikProvider>
  );
}
